/**
 * Gerador do template Praxis (Schmersal/tec.nicum).
 *
 * Referência analisada: "Praxis model.pdf" (63 páginas, A4). O modelo usa capa
 * editorial azul, logos tec.nicum/SCHMERSAL, sumário, lista de abreviaturas,
 * seções metodológicas 1-5 e a seção 6 com cadastro da máquina, lista de partes,
 * checklist técnico/administrativo e fichas individuais de risco.
 *
 * NOTA: este arquivo usa o `escapeLatex`/`escapeLatexComQuebras` SOMENTE para
 * texto vindo do usuário. Strings com comandos LaTeX literais (\hspace, \ding,
 * etc.) NUNCA podem passar por essas funções, senão saem como texto literal.
 */

import { DadosApreciacao, Risco } from '../core/latex-types';
import { escapeLatex, escapeLatexComQuebras } from '../core/latex-utils';

type CorRisco = 'riskGreen' | 'riskYellow' | 'riskOrange' | 'riskRed';

interface ClassificacaoPraxis {
  texto: string;
  cor: CorRisco;
  textoCor: 'black' | 'white';
}

const PRAXIS_ASSETS = {
  cover: 'praxis-cover.jpg',
  tecnicumLogo: 'praxis-tecnicum-logo.jpg',
  schmersalLogo: 'praxis-schmersal-logo.jpg',
  fluxogramaRisco: 'praxis-fluxograma-risco.jpg',
  medidasProtecao: 'praxis-medidas-protecao.jpg',
  exemplosPerigos: 'praxis-exemplos-perigos.jpg',
  plSil: 'praxis-pl-sil.jpg',
  plrDiagrama: 'praxis-plr-diagrama.jpg',
  tabelaGs: 'praxis-tabela-gs.jpg',
  tabelaFe: 'praxis-tabela-fe.jpg',
  tabelaPo: 'praxis-tabela-po.jpg',
  tabelaNp: 'praxis-tabela-np.jpg',
  tabelaHrn: 'praxis-tabela-hrn.jpg',
  legendaChecklist: 'praxis-legenda-checklist.jpg',
  iconMecanico: 'praxis-icon-mecanico.jpg',
  iconEletrico: 'praxis-icon-eletrico.jpg',
  iconErgonomico: 'praxis-icon-ergonomico.jpg',
  iconCheckVerde: 'praxis-icon-check-verde.jpg',
  iconCheckAzul: 'praxis-icon-check-azul.jpg',
  iconCirculoAzul: 'praxis-icon-circulo-azul.jpg',
  iconCirculoVermelho: 'praxis-icon-circulo-vermelho.jpg',
  fallbackPanoramica: 'praxis-fallback-panoramica.jpg',
  fallbackIdentificacao: 'praxis-fallback-identificacao.jpg',
  fallbackRiscoMecanico: 'praxis-fallback-risco-mecanico.jpg',
  fallbackRiscoEletrico: 'praxis-fallback-risco-eletrico.jpg',
} as const;

const ABREVIATURAS_PRAXIS: Array<[string, string]> = [
  ['ABNT', 'Associação Brasileira de Normas Técnicas'],
  ['Cat', 'Categoria de Segurança equivalente a CC'],
  ['CC', 'Control Category (Categoria de Controle), também conhecida como Categoria de Segurança (B, 1, 2, 3, 4); a categoria já decisivamente dita à qualidade relacionada à segurança de um SRP/CS. Enquanto CC B e CC 1 lida com a qualidade do componente usado, categorias mais altas demandam componentes adicionais (canais) que conseguem compensar a falha de componentes individuais.'],
  ['CCF', 'Common Cause Failure (Falhas de Causa Comum): falhas com causa comum nas quais componentes que simultaneamente processam a mesma coisa várias vezes por razões de segurança falham na mesma hora.'],
  ['DC', 'Diagnostic Coverage (Cobertura de Diagnóstico): grau de cobertura de diagnóstico, capacidade para detectar falhas, normalmente automática.'],
  ['EC', 'Europe Community (Comunidade Europeia)'],
  ['EN', 'European Standards (Norma Europeia)'],
  ['F', 'Frequência e/ou tempo de exposição (ABNT NBR ISO 13849-1:2019)'],
  ['FE', 'Frequência de Exposição (HRN)'],
  ['GS', 'Grau de Severidade (HRN)'],
  ['HRN', 'Hazard Rating Number (Número de classificação do perigo)'],
  ['IEC', 'International Electrotechnical Commission (Comissão Internacional de Eletrotécnica)'],
  ['ISO', 'International Organization for Standardization (Organização Internacional de Normalização)'],
  ['MTTFd', 'Mean Time to dangerous Failure (Tempo Médio até a Falha Perigosa). Tempo para que uma falha perigosa de um componente ou mecanismo ocorra; não deve ser confundida com serviço de vida garantido.'],
  ['NBR', 'Norma Brasileira de Referência'],
  ['NP', 'Número de Pessoas expostas ao Risco (HRN)'],
  ['NR', 'Norma Regulamentadora'],
  ['P', 'Possibilidade de evitar o dano (ABNT NBR ISO 13849-1:2019)'],
  ['PFHd', 'Probability of a dangerous Failure per Hour (Probabilidade de Falha Perigosa por Hora)'],
  ['PL', 'Performance Level (Nível de Desempenho) - ABNT NBR ISO 13849-1:2019'],
  ['PLr', 'Required Performance Level (Nível de Desempenho requerido) - ABNT NBR ISO 13849-1:2019'],
  ['PO', 'Probabilidade de Ocorrer (HRN)'],
  ['S', 'Severidade do ferimento (ABNT NBR ISO 13849-1:2019)'],
  ['SIL', 'Safety Integrity Level (Nível de integridade de segurança) - IEC 62061:2015'],
  ['SRB', 'Sicherheits-Relais-Baustein (Relé de Segurança)'],
  ['SRP/CS', 'Safety-Related Parts of Control Systems (Partes de Sistemas de Comando Relacionadas à Segurança)'],
];

const NORMAS_NACIONAIS = [
  'ABNT NBR ISO 12100:2013 - Segurança de máquinas - Princípios gerais de projeto - Apreciação e redução de riscos',
  'ABNT ISO/TR 14121-2:2018 - Segurança de máquinas - Apreciação de riscos - Parte 2: Guia prático e exemplos de métodos',
  'ABNT NBR 13970:1997 - Segurança de máquinas - Temperatura de superfícies acessíveis - Dados ergonômicos',
  'ABNT NBR 14152:1998 - Segurança de máquinas - Dispositivos de comando bimanuais',
  'ABNT NBR 14153:2022 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança',
  'ABNT NBR 14154:1998 - Segurança de máquinas - Prevenção de partida inesperada',
  'ABNT NBR IEC 60204-1:2020 - Segurança de máquinas - Equipamentos Elétricos de Máquinas - Parte 1: Requisitos Gerais',
  'ABNT NBR 5410:2004 - Versão Corrigida: 2008 - Instalações elétricas de baixa tensão',
  'ABNT NBR IEC 60529:2017 - Graus de proteção providos por invólucros (Códigos IP)',
  'ABNT NBR IEC 61439-1:2016 - Conjuntos de manobras e controle de baixa tensão - Parte 1: Regras Gerais',
  'ABNT NBR IEC 61439-3:2017 - Conjuntos de manobras e controle de baixa tensão - Parte 3',
  'ABNT NBR IEC 61439-6:2018 - Conjuntos de manobras e controle de baixa tensão - Parte 2: Sistemas de Linhas Elétricas Pré-Fabricadas',
  'ABNT NBR ISO 13849-1:2019 - Partes de sistemas de comando relacionados à segurança - Parte 1: Princípios gerais para projeto',
  'ABNT NBR ISO 13849-2:2019 - Partes de sistemas de comando relacionados à segurança - Parte 2: Validação',
  'ABNT NBR ISO 13850:2021 - Função de Parada de Emergência',
  'ABNT NBR NM ISO 13854:2003 - Folgas mínimas para evitar esmagamento de partes do corpo humano',
  'ABNT NBR ISO 13855:2013 - Posicionamento dos equipamentos de proteção',
  'ABNT NBR ISO 13857:2021 - Distâncias de segurança para impedir o acesso a zonas de perigo',
  'ABNT NBR ISO 14118:2022 - Prevenção de partida inesperada',
  'ABNT NBR ISO 14119:2021 - Dispositivos de Intertravamento Associados às Proteções',
  'ABNT NBR ISO 14120:2023 - Proteções fixas e móveis - Requisitos gerais para projeto',
  'ABNT NBR ISO 14122:1-4:2023 - Meios de acesso permanentes para máquinas (Partes 1 a 4)',
  'ABNT ISO/TR 24119:2022 - Avaliação de mascaramento de defeitos em conexões em série',
  'ABNT NBR ISO 3864-1:2013 - Símbolos gráficos, cores e sinais de segurança',
  'ABNT NBR ISO 4413:2022 - Sistemas hidráulicos - Regras gerais',
  'ABNT NBR ISO 4414:2012 - Transmissão pneumática - Regras gerais',
  'ABNT NBR NM 272:2002 - Proteções fixas e móveis - Requisitos gerais',
];

const NORMAS_INTERNACIONAIS = [
  'ISO 19353:2019 - Safety of machinery - Fire prevention and fire protection',
  'ISO 20607:2019 - Safety of machinery - Instruction handbook - General drafting principles',
  'ISO 7010:2019 - Graphical symbols - Safety colors and safety signs',
  'IEC 61496-1:2020 - Safety of machinery - Electro-sensitive protective equipment - Part 1',
  'IEC 61496-2:2020 - Electro-sensitive protective equipment - Part 2 (AOPDs)',
  'IEC 61496-3:2018 - Electro-sensitive protective equipment - Part 3 (AOPDDR)',
  'IEC 62046:2018 - Application of protective equipment to detect the presence of persons',
  'IEC 61800-5-1 - Adjustable speed electrical power drive systems - Part 5-1: Safety requirements - Electrical, thermal and energy',
  'IEC 61800-5-2 - Adjustable speed electrical power drive systems - Part 5-2: Safety requirements - Functional',
];

// Códigos oficiais Schmersal/tec.nicum (ver modelo p36-38)
const CHECKLIST_PRAXIS_PADRAO: Array<[string, string]> = [
  ['10', 'Legal: Os requisitos de segurança e saúde locais (do país onde se encontra a máquina) são mais rígidos que os requisitos internos da empresa?'],
  ['11', 'Legal: Caso as normas do país sejam mais rigorosas do que as normas da empresa, estas normas são cumpridas?'],
  ['12', 'Legal: No caso da origem ou destino da máquina ser a Europa, possui declaração de conformidade CE?'],
  ['13', 'Legal: A empresa possui relação atualizada das máquinas e equipamentos à disposição da Auditoria Fiscal do Trabalho?'],
  ['20', 'Área classificada: A máquina se encontra em área classificada para gases inflamáveis (ABNT NBR IEC 60079-10-1)?'],
  ['21', 'Área classificada: Se sim para a pergunta 20, possui estudo/planta com ART de profissional habilitado?'],
  ['22', 'Área classificada: Se sim para a pergunta 20, possui registro de inspeção das instalações elétricas EX?'],
  ['23', 'Área classificada: A máquina se encontra em área classificada para poeiras combustíveis (ABNT NBR IEC 60079-10-2)?'],
  ['24', 'Área classificada: Se sim para a pergunta 23, possui estudo/planta com ART de profissional habilitado?'],
  ['25', 'Área classificada: Se sim para a pergunta 23, possui registro de inspeção das instalações elétricas EX?'],
  ['30', 'Administrativa: A máquina possui Apreciação de Riscos prévia conforme ABNT NBR ISO 12100?'],
  ['31', 'Administrativa: A máquina possui manual de instruções de operação/segurança/manutenção em português?'],
  ['32', 'Administrativa: A máquina possui esquema elétrico atualizado em português?'],
  ['33', 'Administrativa: A máquina possui esquema mecânico atualizado em português?'],
  ['34', 'Administrativa: A máquina possui esquema pneumático em português?'],
  ['35', 'Administrativa: A máquina possui esquema hidráulico atualizado em português?'],
  ['36', 'Administrativa: A máquina possui plano de manutenção preventiva/preditiva?'],
  ['37', 'Administrativa: A máquina possui registro de manutenção corretiva?'],
  ['38', 'Administrativa: A máquina possui plano de bloqueio/etiquetamento (LockOut/TagOut)?'],
  ['39', 'Administrativa: A máquina possui registro de treinamento de operação e segurança?'],
  ['40', 'Administrativa: A máquina possui registro de fornecimento de EPI adequado (Certificado de Aprovação)?'],
  ['41', 'Administrativa: A máquina possui laudo de medição de níveis de ruído?'],
  ['42', 'Administrativa: A máquina possui laudo de medição de iluminância?'],
  ['43', 'Administrativa: A máquina possui laudo de medição de radiação ionizante ou não ionizante?'],
  ['44', 'Administrativa: A máquina possui laudo de substâncias perigosas/FISPQ?'],
  ['45', 'Administrativa: A máquina possui laudo de medição do tempo de parada?'],
  ['46', 'Administrativa: A máquina possui laudo de aterramento elétrico?'],
  ['47', 'Administrativa: A máquina possui autorização para entrada em espaço confinado quando aplicável?'],
  ['48', 'Administrativa: A máquina possui laudo de compatibilidade eletromagnética (EMC)?'],
  ['49', 'Administrativa: A máquina possui laudo de análise de risco ergonômico?'],
  ['50', 'Administrativa: A máquina possui histórico de acidentes de trabalho?'],
  ['51', 'Administrativa: A máquina necessita de operador de empilhadeira para operação ou ajuste?'],
  ['52', 'Administrativa: O operador de empilhadeira possui cartão de autorização para sua condução?'],
  ['53', 'Administrativa: A máquina possui análise/procedimento para trabalho em altura quando aplicável?'],
];

// Títulos oficiais do bloco "Limites da Máquina" (ver modelo p32-33)
const LIMITES_BASE_PRAXIS = [
  'SITE',
  'LOCAL DE INSTALAÇÃO',
  'NOME DA GERÊNCIA',
  'CÓD. EQUIPAMENTO Nº SAP',
  'CAPACIDADE MÁXIMA',
  'MODELO',
  'FABRICANTE',
  'ANO DE FABRICAÇÃO',
  'NÚMERO DE PATRIMÔNIO',
  'CONTRATO/ Nº. OS',
  'DIMENSÕES DO EQUIPAMENTO',
  'AMBIENTE',
  'PISO OPERACIONAL',
  'PESO BRUTO DA MÁQUINA (KG)',
  'ACIONAMENTOS HIDRÁULICOS',
  'ACIONAMENTOS PNEUMÁTICOS',
  'ACIONAMENTOS ELÉTRICOS',
  'POTÊNCIA',
  'Nº. DE MOTORES/ REDUTORES',
  'Nº. DE MOTOREDUTORES',
  'TENSÃO DE ALIMENTAÇÃO',
  'TENSÃO DE COMANDO',
  'CORRENTE',
  'VELOCIDADE MÁXIMA',
  'SISTEMA DE MANUSEIO/ TRANSPORTE/ MOVIMENTAÇÃO DE CARGA/ PEÇA/ PRODUTO',
  'FIXAÇÃO DA MÁQUINA, CRITÉRIO DE AVALIAÇÃO',
];

function formatarDataPorExtenso(data?: string, cidade = 'Boituva'): string {
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  const fonte = data || new Date().toISOString();
  const str = String(fonte).substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split('-').map(Number);
    return `${cidade}, ${dia} de ${meses[mes - 1] || meses[0]} de ${ano}`;
  }
  const hoje = new Date();
  return `${cidade}, ${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`;
}

function formatarDataCurta(data?: string): string {
  if (!data) return '';
  const str = String(data).substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split('-');
    return `${dia}/${mes}/${ano}`;
  }
  return str;
}

function n(value: unknown, casas = 3): string {
  const numero = Number(value);
  if (!Number.isFinite(numero)) return '-';
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });
}

function obterNumeroPraxis(dados: DadosApreciacao): string {
  return dados.numeroDocumento
    || dados.camposCliente?.praxis?.numeroDocumentoPraxis
    || dados.numero
    || 'AAAA/NN-NNNN';
}

function obterClienteFinal(dados: DadosApreciacao): string {
  return dados.camposCliente?.praxis?.clienteFinal || dados.projeto?.nome || 'SENAI';
}

function obterConsultor(dados: DadosApreciacao): { nome: string; funcao: string } {
  const origem = dados.responsavelTecnicoExterno
    || dados.responsavelTecnico
    || dados.executanteExterno
    || dados.executante
    || dados.elaboradorExterno
    || dados.elaborador
    || {};
  const nomeBruto = (origem.nome || 'CONSULTOR TÉCNICO').toString();
  const funcaoBruta = (origem.funcao || origem.cargo || 'CONSULTOR TÉCNICO').toString();
  // Truncar funcao na primeira linha/parêntese para a capa não estourar
  return {
    nome: nomeBruto.trim(),
    funcao: funcaoBruta.trim(),
  };
}

function obterAnoCopyrightPraxis(dados: DadosApreciacao): string {
  return String(dados.camposCliente?.praxis?.anoCopyright || '2025');
}

function funcaoConsultorCurta(funcao: string): string {
  // Pega apenas o primeiro título antes de vírgula/parêntese/quebra de linha
  const limpa = funcao.replace(/\s+/g, ' ').trim();
  const corte = limpa.match(/^[^,(\n;/]+/);
  return (corte ? corte[0] : limpa).trim();
}

function classificarPraxis(hrn?: number): ClassificacaoPraxis {
  const valor = Number(hrn || 0);
  if (valor <= 10) return { texto: 'Desprezível', cor: 'riskGreen', textoCor: 'black' };
  if (valor <= 50) return { texto: 'Baixo, porém significativo', cor: 'riskYellow', textoCor: 'black' };
  if (valor <= 500) return { texto: 'Alto', cor: 'riskOrange', textoCor: 'black' };
  return { texto: 'Inaceitável', cor: 'riskRed', textoCor: 'white' };
}

function imagemOuPlaceholder(url: string | undefined, altura: string, label: string, fallbackAsset?: string): string {
  if (url) {
    return `\\includegraphics[height=${altura},width=0.96\\linewidth,keepaspectratio]{${url}}`;
  }
  if (fallbackAsset) {
    return `\\includegraphics[height=${altura},width=0.96\\linewidth,keepaspectratio]{${fallbackAsset}}`;
  }
  // Placeholder visível para o cliente entender que falta uma foto
  return `\\fbox{\\parbox[c][${altura}][c]{0.92\\linewidth}{\\centering\\textcolor{praxisDarkGray}{\\small\\textit{[${escapeLatex(label)} não disponível]}}}}`;
}

function tipoPerigoIcone(tipo?: string, altura = '0.22cm'): string {
  const t = (tipo || '').toLowerCase();
  if (t.includes('ele') || t.includes('elé')) {
    return `\\includegraphics[height=${altura}]{${PRAXIS_ASSETS.iconEletrico}}`;
  }
  if (t.includes('erg')) {
    return `\\includegraphics[height=${altura}]{${PRAXIS_ASSETS.iconErgonomico}}`;
  }
  return `\\includegraphics[height=${altura}]{${PRAXIS_ASSETS.iconMecanico}}`;
}

function conformidadeMarca(conforme?: boolean): string {
  const asset = conforme ? PRAXIS_ASSETS.iconCheckVerde : PRAXIS_ASSETS.iconCirculoVermelho;
  return `\\includegraphics[height=0.22cm]{${asset}}`;
}

function imagemRiscoFallback(tipo?: string): string {
  const t = (tipo || '').toLowerCase();
  if (t.includes('ele') || t.includes('elé')) return PRAXIS_ASSETS.fallbackRiscoEletrico;
  return PRAXIS_ASSETS.fallbackRiscoMecanico;
}

function bulletsDeTexto(texto: string | undefined, fallback = 'N/I'): string {
  const fonte = (texto || '').trim();
  if (!fonte) {
    return `\\begin{itemize}\\item ${escapeLatex(fallback)}\\end{itemize}`;
  }
  // Aceita "; " ou quebra de linha como separador; senão usa o texto como bullet único
  const partes = fonte
    .split(/\n|;\s+/)
    .map((p) => p.trim().replace(/^[•\-\*•◦]\s*/, ''))
    .filter(Boolean);
  if (partes.length === 0) {
    return `\\begin{itemize}\\item ${escapeLatex(fallback)}\\end{itemize}`;
  }
  return `\\begin{itemize}\n${partes.map((p) => `\\item ${escapeLatex(p)}`).join('\n')}\n\\end{itemize}`;
}

function gerarPreambuloPraxis(dados: DadosApreciacao): string {
  const numeroDocumento = escapeLatex(obterNumeroPraxis(dados));
  const anoCopyright = escapeLatex(obterAnoCopyrightPraxis(dados));
  return `\\documentclass[a4paper,10pt]{article}
\\usepackage{graphicx}
\\DeclareGraphicsExtensions{.png,.jpg,.jpeg,.pdf}
\\usepackage[table]{xcolor}
\\usepackage{array}
\\usepackage{tabularx}
\\usepackage{xltabular}
\\usepackage{longtable}
\\usepackage{colortbl}
\\usepackage{fancyhdr}
\\usepackage{lastpage}
\\usepackage{float}
\\usepackage{enumitem}
\\usepackage{hyperref}
\\usepackage{tocloft}
\\usepackage{fontspec}
\\usepackage{tikz}
\\usetikzlibrary{positioning,calc}
\\usepackage{eso-pic}
\\usepackage{titlesec}
\\usepackage{multicol}
\\usepackage{pifont}
\\usepackage[a4paper, top=2.35cm, left=1.55cm, right=1.35cm, bottom=2.1cm]{geometry}

\\setmainfont{Liberation Sans}
\\definecolor{praxisBlue}{RGB}{0,63,128}
\\definecolor{praxisDarkBlue}{RGB}{0,55,123}
\\definecolor{praxisLightBlue}{RGB}{0,151,204}
\\definecolor{praxisGray}{RGB}{235,238,240}
\\definecolor{praxisGrayLight}{RGB}{246,247,249}
\\definecolor{praxisDarkGray}{RGB}{60,65,70}
\\definecolor{praxisGreen}{RGB}{82,142,67}
\\definecolor{praxisOrange}{RGB}{222,135,32}
\\definecolor{praxisRed}{RGB}{198,42,42}
\\definecolor{praxisPurple}{RGB}{120,57,153}
\\definecolor{riskGreen}{HTML}{B7E1A1}
\\definecolor{riskYellow}{HTML}{FFF2A8}
\\definecolor{riskOrange}{HTML}{F6B26B}
\\definecolor{riskRed}{HTML}{E06666}

\\setlength\\parindent{0pt}
\\setlength{\\parskip}{4pt}
\\renewcommand{\\arraystretch}{1.22}
\\setlength{\\tabcolsep}{5pt}
\\setlist[itemize]{leftmargin=*, itemsep=1pt, topsep=2pt}

\\pagestyle{fancy}
\\fancyhf{}
\\setlength{\\headheight}{1.65cm}
\\fancyhead[L]{\\raisebox{-0.15cm}{\\includegraphics[height=1.0cm]{${PRAXIS_ASSETS.tecnicumLogo}}}}
\\fancyhead[R]{\\raisebox{-0.10cm}{\\includegraphics[height=1.15cm]{${PRAXIS_ASSETS.schmersalLogo}}}}
\\fancyfoot[L]{\\scriptsize \\textcopyright\\ ${anoCopyright} ACE SCHMERSAL, Todos os Direitos Reservados. É expressamente proibida a cópia e/ou reprodução deste documento.\\\\[-1pt]\\textbf{${numeroDocumento}}}
\\fancyfoot[R]{\\textcolor{praxisDarkBlue}{\\rule{1.5pt}{0.9cm}}\\hspace{0.35cm}\\textbf{\\thepage\\ of \\pageref{LastPage}}}
\\renewcommand{\\headrulewidth}{0pt}
\\renewcommand{\\footrulewidth}{0pt}

% Redefine o pagestyle "plain" (aplicado pelo \\tableofcontents e por \\section* com \\thispagestyle{plain})
% para manter o cabeçalho e rodapé Praxis em TODAS as páginas, incluindo o sumário.
\\fancypagestyle{plain}{%
  \\fancyhf{}%
  \\fancyhead[L]{\\raisebox{-0.15cm}{\\includegraphics[height=1.0cm]{${PRAXIS_ASSETS.tecnicumLogo}}}}%
  \\fancyhead[R]{\\raisebox{-0.10cm}{\\includegraphics[height=1.15cm]{${PRAXIS_ASSETS.schmersalLogo}}}}%
  \\fancyfoot[L]{\\scriptsize \\textcopyright\\ ${anoCopyright} ACE SCHMERSAL, Todos os Direitos Reservados. É expressamente proibida a cópia e/ou reprodução deste documento.\\\\[-1pt]\\textbf{${numeroDocumento}}}%
  \\fancyfoot[R]{\\textcolor{praxisDarkBlue}{\\rule{1.5pt}{0.9cm}}\\hspace{0.35cm}\\textbf{\\thepage\\ of \\pageref{LastPage}}}%
  \\renewcommand{\\headrulewidth}{0pt}%
  \\renewcommand{\\footrulewidth}{0pt}%
}

\\titleformat{\\section}{\\Large\\bfseries\\color{praxisDarkGray}}{\\thesection}{0.5em}{}
\\titleformat{\\subsection}{\\large\\bfseries\\color{praxisDarkGray}}{\\thesubsection}{0.5em}{}
\\titleformat{\\subsubsection}{\\normalsize\\bfseries\\color{praxisDarkGray}}{\\thesubsubsection}{0.4em}{}
\\titlespacing*{\\section}{0pt}{8pt}{6pt}
\\titlespacing*{\\subsection}{0pt}{7pt}{4pt}
\\titlespacing*{\\subsubsection}{0pt}{6pt}{3pt}

\\hypersetup{colorlinks=false,pdfborder={0 0 0},linktoc=all}
\\renewcommand{\\contentsname}{SUMÁRIO}
\\renewcommand{\\cfttoctitlefont}{\\hfill\\Large\\bfseries\\color{praxisDarkGray}}
\\renewcommand{\\cftaftertoctitle}{\\hfill}
\\setlength{\\cftbeforesecskip}{3pt}
\\setlength{\\cftbeforesubsecskip}{1pt}

% Faixas e títulos coloridos do estilo Schmersal
\\newcommand{\\praxisTitle}[1]{\\vspace{0.18cm}\\noindent\\colorbox{praxisBlue}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\centering\\color{white}\\bfseries\\strut #1}}\\vspace{0.12cm}}
\\newcommand{\\praxisBar}[1]{\\vspace{0.12cm}\\noindent\\colorbox{praxisGray}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\bfseries\\strut #1}}\\vspace{0.12cm}}
\\newcommand{\\praxisBarBlue}[1]{\\vspace{0.12cm}\\noindent\\colorbox{praxisBlue}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\color{white}\\bfseries\\strut #1}}\\vspace{0.12cm}}
\\newcommand{\\praxisBarLight}[1]{\\vspace{0.05cm}\\noindent\\colorbox{praxisGrayLight}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\bfseries\\strut #1}}\\vspace{0.05cm}}
\\newcommand{\\xis}{\\textbf{X}}
`;
}

function gerarCapaPraxis(dados: DadosApreciacao, _riscos: Risco[]): string {
  const numeroDocumento = obterNumeroPraxis(dados);
  const cliente = obterClienteFinal(dados);
  const consultor = obterConsultor(dados);
  const anoCopyright = obterAnoCopyrightPraxis(dados);
  const funcaoCurta = funcaoConsultorCurta(consultor.funcao);
  const codigo = dados.identificacaoMaquina || dados.ativo?.refSismanaNovo || dados.numeroEmitente || '';
  const maquina = dados.nomeMaquina || dados.maquina?.nome || dados.maquina?.descricao || dados.titulo || 'Máquina';
  // Título principal da capa: <consultor iniciais> _ <identificação> (ex: "I. CONTINI _ GC-3130KP-02")
  const iniciaisConsultor = consultor.nome
    .split(/\s+/)
    .filter(Boolean)
    .map((p, i, arr) => (i === 0 ? `${p[0]?.toUpperCase()}.` : (i === arr.length - 1 ? p.toUpperCase() : '')))
    .filter(Boolean)
    .join(' ');
  const tituloCapa = codigo
    ? `${iniciaisConsultor ? iniciaisConsultor + ' _ ' : ''}${codigo}`
    : iniciaisConsultor || maquina;
  const descritor = dados.camposCliente?.praxis?.descricaoServico || dados.titulo || (codigo ? `${codigo} - ${maquina}` : maquina);

  return `
\\hypersetup{pageanchor=false}
\\begin{titlepage}
\\thispagestyle{empty}
\\begin{tikzpicture}[remember picture,overlay]
  \\node[anchor=center,inner sep=0] at (current page.center)
    {\\includegraphics[width=\\paperwidth,height=\\paperheight]{${PRAXIS_ASSETS.cover}}};
  % O fundo extraído do PDF contém a foto e a faixa azul. O modelo final cobre
  % o logo institucional antigo e reposiciona os logos oficiais por cima.
  \\fill[white] ([xshift=-8.15cm,yshift=-1.55cm]current page.north east) rectangle ([xshift=-0.35cm,yshift=-5.20cm]current page.north east);
  \\node[anchor=west] at ([xshift=1.1cm,yshift=-3.0cm]current page.north west)
    {\\includegraphics[height=1.6cm]{${PRAXIS_ASSETS.tecnicumLogo}}};
  \\node[anchor=north east] at ([xshift=-1.15cm,yshift=-2.35cm]current page.north east)
    {\\includegraphics[height=1.45cm]{${PRAXIS_ASSETS.schmersalLogo}}};
  \\node[anchor=west,text=white,align=left,text width=13.5cm] at ([xshift=1.55cm,yshift=14.3cm]current page.south west)
    {{\\fontsize{30}{34}\\selectfont\\bfseries ${escapeLatex(tituloCapa)}}\\\\[1.0cm]
     {\\fontsize{17}{20}\\selectfont ${escapeLatex(cliente)}}\\\\[0.35cm]
     {\\fontsize{11}{14}\\selectfont ${escapeLatex(descritor)}}};
  \\node[anchor=east,text=white,align=right,text width=8.8cm] at ([xshift=-1.35cm,yshift=7.9cm]current page.south east)
    {{\\fontsize{29}{34}\\selectfont\\bfseries ${escapeLatex(numeroDocumento)}}\\\\[0.55cm]
     \\textbf{CONTATO}\\\\[1.0cm]
     \\textbf{CRIADO POR}\\\\
     ${escapeLatex(consultor.nome.toUpperCase())}\\\\
     ${escapeLatex(funcaoCurta.toUpperCase())}\\\\[0.85cm]
     \\textbf{ACE SCHMERSAL}\\\\
     \\textbf{Eletroeletrônica Industrial Ltda.}\\\\
     CNPJ: 61.854.147/0001-33\\\\
     Avenida Brasil, 815\\\\
     Jd. Esplanada - CEP 18550-000\\\\
     Boituva/SP - Brasil};
  \\node[anchor=south,text=white] at ([yshift=0.8cm]current page.south)
    {\\textbf{\\textcopyright\\ ${escapeLatex(anoCopyright)} ACE SCHMERSAL, Todos os Direitos Reservados.}};
\\end{tikzpicture}
\\end{titlepage}
% \\end{titlepage} já faz \\newpage internamente — adicionar \\clearpage
% aqui causaria uma página em branco extra entre a capa e o TOC.
\\hypersetup{pageanchor=true}
\\pagestyle{fancy}
`;
}

function gerarListaAbreviaturas(): string {
  const linhas = ABREVIATURAS_PRAXIS
    .map(([sigla, descricao]) => `${escapeLatex(sigla)} & ${escapeLatex(descricao)} \\\\`)
    .join('\n\\hline ');

  // Usar longtable para que o conteúdo possa fluir entre páginas se necessário,
  // evitando o Overfull \\vbox que estava forçando uma página em branco antes.
  return `
\\section*{LISTA DE ABREVIATURAS}
\\addcontentsline{toc}{section}{LISTA DE ABREVIATURAS}
{\\small
\\begin{longtable}{>{\\bfseries\\color{praxisDarkGray}}p{2.1cm}p{\\dimexpr\\linewidth-2.4cm\\relax}}
${linhas}
\\end{longtable}
}
\\clearpage
`;
}

function gerarInformacoesDocumento(dados: DadosApreciacao): string {
  const contatosCliente = dados.camposCliente?.praxis?.contatosCliente || [];
  const contatosSchmersal = dados.camposCliente?.praxis?.contatosSchmersal || [];
  const consultor = obterConsultor(dados);

  const linhasCliente = contatosCliente.length > 0
    ? contatosCliente.map((c: any) => `${escapeLatex(c.nome || '')} & ${escapeLatex(c.funcao || '')} & ${escapeLatex(c.contato || '')} \\\\ \\hline`).join('\n')
    : ` & & \\\\ \\hline`;
  const linhasSchmersal = contatosSchmersal.length > 0
    ? contatosSchmersal.map((c: any) => `${escapeLatex(c.nome || '')} & ${escapeLatex(c.funcao || '')} & ${escapeLatex(c.contato || '')} \\\\ \\hline`).join('\n')
    : `${escapeLatex(consultor.nome.toUpperCase())} & ${escapeLatex(funcaoConsultorCurta(consultor.funcao).toUpperCase())} &  \\\\ \\hline`;

  const normasNacionaisCliente = dados.camposCliente?.praxis?.normasNacionais;
  const normasInternacionaisCliente = dados.camposCliente?.praxis?.normasInternacionais;
  const normasNacionais = (Array.isArray(normasNacionaisCliente) && normasNacionaisCliente.length > 0
    ? [...NORMAS_NACIONAIS, ...normasNacionaisCliente]
    : NORMAS_NACIONAIS)
    .map((norma: string) => `\\item ${escapeLatex(norma)}`).join('\n');
  const normasInternacionais = (Array.isArray(normasInternacionaisCliente) && normasInternacionaisCliente.length > 0
    ? [...NORMAS_INTERNACIONAIS, ...normasInternacionaisCliente]
    : NORMAS_INTERNACIONAIS)
    .map((norma: string) => `\\item ${escapeLatex(norma)}`).join('\n');

  return `
\\section{INFORMAÇÕES DO DOCUMENTO}
Este documento não deve ser reproduzido sem um acordo por escrito da companhia ACE SCHMERSAL Eletroeletrônica Industrial Ltda, inscrita sob CNPJ 61.854.147/0001-33, denominada a partir de agora nesse documento como SCHMERSAL ou pelo uso de sistemas eletrônicos em qualquer forma (fotocópia, microfilme ou outro procedimento) para ser processado, multiplicado e distribuído.

\\textbf{O documento destina-se exclusivamente para o uso do cliente e do contratante.}

Para uso interno na planta pelo cliente, a duplicação e distribuição do documento são permissíveis também sem acordo da SCHMERSAL.

\\subsection{Conteúdos do documento}
Neste documento a abordagem geral é descrita pela produção de uma apreciação de riscos e foram construídas na produção (fábrica) onde a(s) máquina(s) se encontra(m), ou seja, no processo de produção da(s) mesma(s).

Para uma rápida visão geral os seguintes capítulos são recomendados:
\\begin{itemize}
\\item Capítulo 2: Obrigações Legais e abordagem usada durante a Apreciação de Risco(s).
\\item Capítulo 3: Exemplos de perigos associados às máquinas.
\\item Capítulo 4: Critérios para Apreciação de Riscos das máquinas.
\\item Capítulo 6: Apreciação de Riscos das máquinas.
\\end{itemize}

\\subsection{Relação de contatos no cliente e na SCHMERSAL}

\\praxisBarBlue{PESSOA DE CONTATO NO CLIENTE}
\\begin{tabularx}{\\linewidth}{|X|X|X|}
\\hline
\\rowcolor{praxisGray}\\textbf{Nome} & \\textbf{Função} & \\textbf{Dados de contato} \\\\ \\hline
${linhasCliente}
\\end{tabularx}

\\vspace{0.35cm}
\\praxisBarBlue{PESSOAS DE CONTATO NA SCHMERSAL}
\\begin{tabularx}{\\linewidth}{|X|X|X|}
\\hline
\\rowcolor{praxisGray}\\textbf{Nome} & \\textbf{Função} & \\textbf{Dados de contato} \\\\ \\hline
${linhasSchmersal}
\\end{tabularx}

\\subsection{Referência Técnica sobre as Normas de Segurança}
Conforme necessidades observadas, a SCHMERSAL indica através desta proposta as proteções utilizando os dispositivos de segurança citados nas normas abaixo. Estas sugestões vêm atender as seguintes normas de segurança:

\\textbf{NACIONAIS}
\\begin{itemize}
${normasNacionais}
\\end{itemize}

\\textbf{INTERNACIONAIS}
\\begin{itemize}
${normasInternacionais}
\\end{itemize}
\\clearpage
`;
}

function gerarInformacoesGerais(): string {
  return `
\\section{INFORMAÇÕES GERAIS PARA AVALIAÇÃO DE RISCO}
\\subsection{Obrigações legais}
A Norma Regulamentadora NR-12 - Segurança no Trabalho em Máquinas e Equipamentos e seus anexos definem referências técnicas, princípios fundamentais e medidas de proteção para garantir a saúde e integridade física dos trabalhadores e estabelece requisitos mínimos para prevenção de acidentes e doenças do trabalho nas fases de projeto e de utilização de máquinas e equipamentos de todos os tipos, e ainda à sua fabricação, importação, comercialização, exposição e cessão a qualquer título, em todas as atividades econômicas, sem prejuízo da observância do disposto nas demais Normas Regulamentadoras - NR aprovadas pela Portaria nº 3.214, de 8 de junho de 1978, nas normas técnicas vigentes e, na ausência ou omissão destas, nas normas internacionais aplicáveis.

A Diretiva Europeia de Máquinas 2006/42/EC é uma versão revisada da Diretiva Europeia de Máquinas, cuja primeira versão foi adotada em 1989. A nova Diretiva é aplicável desde 29 de dezembro de 2009. A Diretiva tem o objetivo duplo de harmonizar a saúde e os requisitos de segurança aplicáveis às máquinas na base de alto nível de proteção de saúde e segurança, enquanto assegurando a circulação livre de máquinas no mercado da Comunidade Europeia.

A Diretiva Europeia de Máquinas estipula que as máquinas não devem representar um risco (apreciação de risco de acordo com ABNT NBR ISO 12100:2013). Assumindo que não há risco zero na tecnologia, o objetivo é alcançar um risco residual aceitável. Se a segurança é dependente dos sistemas de controle, esses devem ser projetados para que a probabilidade de erros funcionais seja suficientemente baixa.

O fabricante de máquinas ou seu representante autorizado deve assegurar que a apreciação de riscos é cumprida. Pelo processo interativo de apreciação e redução de riscos, o fabricante ou seu representante autorizado devem:
\\begin{itemize}
\\item Determinar os limites da máquina, os quais incluem o uso previsto e qualquer mau uso previsível;
\\item Identificar perigos que possam ser gerados pela máquina e situações associadas ao perigo;
\\item Estimar os riscos, levando em consideração a severidade de possíveis lesões e danos à saúde e a probabilidade de seu acontecimento;
\\item Avaliar os riscos, visando determinar quando a redução de risco é requerida, de acordo com os objetivos da Norma Regulamentadora NR-12;
\\item Eliminar os perigos ou reduzir os riscos associados com os perigos por aplicação de medidas protetoras.
\\end{itemize}

\\subsection{Procedimento para elaboração deste documento de Apreciação de Riscos}

\\subsubsection*{2.2.1 definir os limites de uso da máquina (ver 5.3.2, ABNT NBR 12100:2013)}
Limites de uso incluem o uso devido da máquina bem como a má utilização de formas razoavelmente previsíveis. Aspectos a serem levados em consideração incluem:
\\begin{itemize}
\\item Os diferentes modos de operação e diferentes procedimentos de intervenção para os usuários, incluindo intervenções exigidas pela má utilização da máquina;
\\item O uso da máquina (industrial, não industrial e doméstico) por pessoas identificadas por gênero, idade, mão de uso dominante, ou habilidades físicas limitadas (visual, incapacidade auditiva, tamanho, força, etc.);
\\item Os níveis antecipados de treinamento, experiência ou habilidade do usuário, incluindo: Operadores; Equipe de manutenção ou técnicos; Aprendizes e treinandos; e Público em geral;
\\item Exposição de outras pessoas aos perigos associados à máquina, quando isto possa ser razoavelmente previsto (pessoas próximas, pessoal de administração, visitantes e público em geral, incluindo crianças).
\\end{itemize}

\\subsubsection*{2.2.2 definir os limites de espaço da máquina (ver 5.3.3, ABNT NBR 12100:2013)}
Aspectos a serem considerados para determinação dos limites de espaço incluem: cursos de movimento; espaços destinados a pessoas que interagem com a máquina, tanto em operação como manutenção; interação humana tal como a interface homem-máquina; e conexão da máquina com as fontes de suprimento de energia.

\\subsubsection*{2.2.3 definir os limites de tempo da máquina (ver 5.3.4, ABNT NBR 12100:2013)}
Aspectos a serem considerados para determinação dos limites de tempo incluem a vida útil da máquina e/ou de alguns de seus componentes (ferramental, partes que podem se desgastar, componentes eletromecânicos, etc.), levando-se em consideração o uso devido da máquina e a má utilização de forma razoavelmente previsível, e intervalos de serviço recomendados.

\\subsubsection*{2.2.4 definir outros limites da máquina (ver 5.3.5, ABNT NBR 12100:2013)}
Exemplos de outros limites incluem: propriedades dos materiais a serem processados; limpeza e organização - o nível de limpeza exigido; meio ambiente - condições máximas e mínimas de temperatura recomendadas, possibilidade de operação da máquina em ambientes externos ou internos, clima seco ou úmido, incidência direta da luz solar, tolerância à poeira e líquidos, etc.

\\subsubsection*{2.2.5 determinar todas as áreas perigosas}
Identifica-se o tipo da máquina, seu funcionamento e localizam-se as áreas onde os perigos se encontram.

\\subsubsection*{2.2.6 identificar perigos}
Identificam-se os perigos de acordo com a ABNT NBR ISO 12100:2013 descritos no capítulo 3 deste documento e, se possível, utilizando normas específicas da máquina, inclusive normas "tipo C" devem ser consideradas.

\\subsubsection*{2.2.7 estimar riscos de acordo com ABNT NBR ISO 12100:2013}
Estimam-se os riscos através da metodologia HRN (Hazard Rating Number), descrita no capítulo 4 deste documento.

\\subsubsection*{2.2.8 avaliar os riscos e, se necessário, propor medidas para mitigação}
\\begin{itemize}
\\item Segurança inerente através de projetos e medidas construtivas - os projetos elétricos, mecânicos, hidráulicos devem prever a segurança intrínseca determinada por norma e incluir as medidas construtivas de segurança;
\\item Medidas técnicas protetoras - verificar a existência de componentes de segurança, tais como proteções fixas e móveis, dispositivos de intertravamento, dispositivos de monitoramento e controle de segurança;
\\item Medidas informativas de uso - aplicar medidas para adequação dos manuais de informação de segurança da máquina, procedimentos de segurança, treinamento de segurança e sinalização que estão correlacionados entre si.
\\end{itemize}

\\subsubsection*{2.2.9 documentação de resultados (ver seção 7, ABNT NBR ISO 12100:2013)}
A documentação deve demonstrar o procedimento que foi seguido e os resultados obtidos: máquina avaliada, premissas e condições adotadas, perigos e situações perigosas identificadas, dados utilizados, medidas implementadas, riscos residuais e formulários preenchidos.

\\subsection{Representação esquemática do processo de redução de riscos}
\\begin{center}
\\includegraphics[width=0.96\\linewidth,height=17cm,keepaspectratio]{${PRAXIS_ASSETS.fluxogramaRisco}}

\\small \\textbf{Figura 1:} Representação do processo iterativo de apreciação e redução de riscos.
\\end{center}
\\clearpage
`;
}

function gerarExemplosPerigos(): string {
  const perigos = [
    ['Mecânicos', 'esmagamento, corte, cisalhamento, impacto, enroscar, partes móveis e elementos rotativos'],
    ['Elétricos', 'contato direto/indireto, partes vivas, falhas de isolamento e curto-circuito'],
    ['Térmicos', 'contato com superfícies quentes ou frias'],
    ['Ergonômicos', 'posturas inadequadas, esforços, acesso e visibilidade insuficientes'],
    ['Ruído e vibração', 'exposição ocupacional e efeitos sobre operação/manutenção'],
    ['Materiais e substâncias', 'poeiras, vapores, gases, fluidos e agentes químicos'],
  ];
  const linhas = perigos.map(([origem, consequencias]) => `${escapeLatex(origem)} & ${escapeLatex(consequencias)} \\\\ \\hline`).join('\n');

  return `
\\section{EXEMPLOS DE PERIGOS ASSOCIADOS COM AS MÁQUINAS}
\\begin{center}
\\includegraphics[width=0.86\\linewidth,height=16cm,keepaspectratio]{${PRAXIS_ASSETS.exemplosPerigos}}
\\end{center}

\\vspace{0.25cm}
\\begin{tabularx}{\\linewidth}{|p{4cm}|X|}
\\hline
\\rowcolor{praxisGray}\\textbf{Origem} & \\textbf{Potenciais consequências} \\\\ \\hline
${linhas}
\\end{tabularx}

\\subsection{Exemplos de perigos típicos}
A lista acima é orientativa e deve ser complementada conforme a condição real observada em campo. Perigos típicos a serem considerados:
\\begin{itemize}
\\item Esmagamento, cisalhamento, corte, decepamento, perfuração;
\\item Enroscamento, prisão por arrasto, fricção, abrasão;
\\item Ejeção de fluido/sólido a alta pressão ou alta velocidade;
\\item Choque elétrico, queimadura por arco, escalda;
\\item Contato com superfícies quentes ou frias;
\\item Ruído e vibração - perda auditiva, fadiga, perda de consciência;
\\item Substâncias perigosas (gases, poeiras, fumaças, líquidos);
\\item Postura inadequada, esforço repetitivo, alcance excessivo;
\\item Falhas em sistemas de comando ou monitoramento.
\\end{itemize}
\\clearpage
`;
}

function gerarCriteriosHrn(): string {
  return `
\\section{AVALIAÇÕES DE RISCO E PROPOSTAS DE REDUÇÃO DE RISCO}
\\subsection{Critérios para Avaliação de Risco}
Os riscos são avaliados considerando severidade, frequência de exposição, possibilidade/probabilidade de ocorrência e número de pessoas expostas, conforme metodologias reconhecidas pelas normas ABNT NBR ISO 12100:2013 e ABNT NBR ISO 13849-1:2019.

\\subsection{Categorização de Segurança}
Para funções de segurança são considerados os parâmetros de severidade (S), frequência e tempo de exposição (F) e possibilidade de evitar o dano (P), conforme ABNT NBR ISO 13849-1:2019, para determinação do nível de desempenho requerido (PLr) e a categoria correspondente (B, 1, 2, 3 ou 4).

\\subsubsection*{4.2.1 Severidade S1 e S2}
\\begin{itemize}
\\item \\textbf{S1} - Lesão leve (normalmente reversível): hematomas, escoriações, cortes superficiais, ferimentos que necessitam de primeiros socorros;
\\item \\textbf{S2} - Lesão grave (normalmente irreversível) incluindo morte: fraturas, mutilações, queimaduras profundas, lesões fatais.
\\end{itemize}

\\subsubsection*{4.2.2 Frequência F1 e F2}
\\begin{itemize}
\\item \\textbf{F1} - Exposição rara ou pouco frequente e/ou tempo de exposição curto;
\\item \\textbf{F2} - Exposição frequente a contínua e/ou tempo de exposição longo.
\\end{itemize}

\\subsubsection*{4.2.3 Probabilidade de evitar o dano P1 e P2}
\\begin{itemize}
\\item \\textbf{P1} - Possível evitar o dano em determinadas condições;
\\item \\textbf{P2} - Quase impossível evitar o dano.
\\end{itemize}

\\subsubsection*{4.2.4 Determinação do nível de performance requerido (PLr)}
O nível de desempenho requerido é obtido pela combinação dos parâmetros S, F e P, resultando em PLr de \\textbf{a} (mais baixo) a \\textbf{e} (mais alto). A correspondência com Categoria de Segurança, PFHd e SIL é apresentada no gráfico abaixo.

\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=5.8cm,keepaspectratio]{${PRAXIS_ASSETS.plrDiagrama}}
\\end{center}

\\subsection{Especificações de categorias de segurança}
\\subsubsection*{4.3.1 Categoria de Segurança B}
Sistemas projetados e construídos com componentes selecionados, conforme normas relevantes aplicáveis, suportando as influências esperadas.

\\subsubsection*{4.3.2 Categoria de Segurança 1}
Sistemas com requisitos da categoria B, utilizando componentes e princípios de segurança bem testados.

\\subsubsection*{4.3.3 Categoria de Segurança 2}
Sistemas com requisitos da categoria 1, com a função de segurança sendo testada periodicamente pelo sistema de comando.

\\subsubsection*{4.3.4 Categoria de Segurança 3}
Sistemas com redundância e diagnóstico, onde uma falha simples não leva à perda da função de segurança, e algumas falhas (não todas) são detectadas.

\\subsubsection*{4.3.5 Categoria de Segurança 4}
Sistemas com redundância e diagnóstico onde uma falha simples não leva à perda da função de segurança e é detectada antes que uma nova falha ocorra.

\\subsection{Estimativa de Riscos}
A SCHMERSAL utiliza o HRN (Hazard Rating Number), método numérico para estimar o nível de risco.

\\begin{center}
\\Large\\textbf{HRN = GS $\\times$ FE $\\times$ PO $\\times$ NP}
\\end{center}

\\subsubsection*{4.4.1 Grau de Severidade (GS)}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=6.2cm,keepaspectratio]{${PRAXIS_ASSETS.tabelaGs}}
\\end{center}

\\subsubsection*{4.4.2 Frequência de Exposição (FE)}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=5.0cm,keepaspectratio]{${PRAXIS_ASSETS.tabelaFe}}
\\end{center}

\\subsubsection*{4.4.3 Probabilidade de Ocorrência (PO)}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=5.8cm,keepaspectratio]{${PRAXIS_ASSETS.tabelaPo}}
\\end{center}

\\subsubsection*{4.4.4 Número de Pessoas (NP)}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=4.4cm,keepaspectratio]{${PRAXIS_ASSETS.tabelaNp}}
\\end{center}

\\vspace{0.4cm}
\\textbf{Classificação dos riscos:}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=4.0cm,keepaspectratio]{${PRAXIS_ASSETS.tabelaHrn}}
\\end{center}

\\subsection{Hierarquia de medidas de redução de risco}
\\begin{center}
\\includegraphics[width=0.92\\linewidth,height=9cm,keepaspectratio]{${PRAXIS_ASSETS.medidasProtecao}}
\\end{center}
\\clearpage
`;
}

function gerarTermosDefinicoes(): string {
  return `
\\section{TERMOS E DEFINIÇÕES DA ABNT NBR ISO 12100:2013}
\\subsection{Proteção}
Barreira física projetada como parte da máquina, para fornecer proteção.

\\textit{NOTA 1:} Uma proteção pode atuar: sozinha, quando é efetiva somente quando estiver "fechada" (proteção móvel) ou "firmemente fixada no local" (proteções fixas); ou em conjunto com um dispositivo de intertravamento com ou sem bloqueio, garantindo a segurança em qualquer posição da proteção.

\\textit{NOTA 2:} Dependendo de seu projeto, uma proteção pode ser chamada caixa, blindagem, tampa, tela, porta, carenagem.

\\subsubsection*{5.1.1 Proteção fixa}
Proteção fixada de tal modo (parafusos, porcas, soldagem) que somente poderá ser aberta ou removida com o uso de ferramentas ou destruição do meio de fixação.

\\subsubsection*{5.1.2 Proteção móvel}
Proteção que pode ser aberta sem o uso de ferramentas.

\\subsubsection*{5.1.3 Proteção ajustável}
Proteção fixa ou móvel que pode ser ajustada como um todo ou que incorpora parte(s) ajustável(is).

\\subsubsection*{5.1.4 Proteção com intertravamento}
Proteção associada a um dispositivo de intertravamento que, em conjunto com o sistema de controle da máquina: impede a execução das funções perigosas até que a proteção esteja fechada; executa o comando de parada se a proteção for aberta; e quando a proteção é fechada, permite a execução das funções perigosas, mas o fechamento não inicia por si só sua operação. (Ver ISO 14119.)

\\subsubsection*{5.1.5 Proteção com intertravamento e bloqueio}
Proteção associada a um dispositivo de intertravamento e a um dispositivo de bloqueio que mantém a proteção fechada e bloqueada até que os riscos das funções perigosas tenham cessado. (Ver ISO 14119.)

\\subsection{Perigos elétricos}
Para o projeto dos equipamentos elétricos de máquinas, a NBR IEC 60204-1:2020 contém disposições gerais referentes à seccionamento e manobra de circuitos elétricos, bem como a proteção contra choques elétricos.

\\subsection{Painel Elétrico}
\\subsubsection*{5.3.1 Instalações e dispositivos elétricos}
As instalações elétricas das máquinas e equipamentos devem ser projetadas e mantidas de modo a prevenir, por meios seguros, os perigos de choque elétrico, incêndio, explosão e outros tipos de acidentes, conforme previsto na NR-10. Devem ser aterradas, conforme as normas técnicas oficiais vigentes, as instalações, carcaças, invólucros, blindagens ou partes condutoras das máquinas e equipamentos que não façam parte dos circuitos elétricos, mas que possam ficar sob tensão.

Os quadros de energia das máquinas e equipamentos devem possuir porta de acesso, sinalização de perigo, restrição de acesso, ser mantidos em bom estado de conservação, possuir proteção e identificação dos circuitos, atender ao grau de proteção adequado em função do ambiente de uso.

\\subsubsection*{5.3.2 Perigos elétricos (itens importantes e relevantes da NR-10)}
\\begin{tabularx}{\\linewidth}{|p{1.6cm}|X|}
\\hline
\\rowcolor{praxisGray}\\textbf{Item} & \\textbf{Tema} \\\\ \\hline
10.2 & Medidas de controle \\\\ \\hline
10.2.8 & Medidas de proteção coletiva \\\\ \\hline
10.2.9 & Medidas de proteção individual \\\\ \\hline
10.3 & Segurança em projetos \\\\ \\hline
10.4 & Segurança na construção, montagem, operação e manutenção \\\\ \\hline
10.5 & Segurança em instalações elétricas desenergizadas \\\\ \\hline
10.6 & Segurança em instalações elétricas energizadas \\\\ \\hline
10.7 & Trabalhos envolvendo Alta Tensão (AT) \\\\ \\hline
10.8 & Habilitação, qualificação, capacitação e autorização dos trabalhadores \\\\ \\hline
10.9 & Proteção contra incêndio e explosão \\\\ \\hline
10.10 & Sinalização de segurança \\\\ \\hline
10.11 & Procedimentos de trabalho \\\\ \\hline
10.12 & Situação de emergência \\\\ \\hline
\\end{tabularx}
\\clearpage
`;
}

function gerarLimitesMaquina(dados: DadosApreciacao): string {
  const limites = dados.camposCliente?.praxis?.limitesMaquina;
  if (Array.isArray(limites) && limites.length > 0) {
    return limites.map((item: any, index: number) => {
      const codigo = item.codigo || String(index + 1).padStart(2, '0');
      const titulo = item.titulo || LIMITES_BASE_PRAXIS[index] || 'ITEM';
      const texto = item.texto || 'N/I';
      return `${escapeLatex(codigo)}----${escapeLatex(titulo)}: ${escapeLatexComQuebras(texto)}\\\\`;
    }).join('\n');
  }

  const valores = [
    dados.maquina?.site || obterClienteFinal(dados),
    dados.maquina?.local || dados.setorMaquina || 'N/I',
    dados.maquina?.area || 'N/I',
    dados.ativo?.refSismanaNovo || dados.ativo?.refSismanaAntigo || 'N/I',
    dados.capacidadeCorreia || 'N/I',
    dados.tipoMaquina || dados.maquina?.modelo || 'N/I',
    dados.maquina?.fabricante || 'N/I',
    dados.maquina?.ano ? String(dados.maquina.ano) : 'N/I',
    'N/I',
    dados.demanda || 'N/I',
    dados.comprimentoHorizontal || 'N/I',
    dados.limitesAmbientais || 'INDUSTRIAL',
    'N/I',
    'N/I',
    '( )EXISTENTE (X) NÃO EXISTENTE',
    '( )EXISTENTE (X) NÃO EXISTENTE',
    '(X)EXISTENTE ( ) NÃO EXISTENTE',
    dados.potencia || 'N/I',
    'N/I',
    'N/I',
    dados.tensaoAcionamento || 'N/I',
    dados.tensaoComando || 'N/I',
    'N/I',
    dados.velocidadeCorreia || 'N/I',
    'A) ALIMENTAÇÃO FRONTAL: ( )  B) ROBÔ/ESTEIRA: ( )  C) OUTROS: (X)',
    'A) PEDESTAL: ( )  B) RODÍZIOS: ( )  C) FUNDAÇÃO RÍGIDA/PARAFUSO: ( )',
  ];

  return LIMITES_BASE_PRAXIS.map((titulo, index) => {
    const codigo = String(index + 1).padStart(2, '0');
    return `${codigo}----${escapeLatex(titulo)}: ${escapeLatexComQuebras(valores[index] || 'N/I')}\\\\`;
  }).join('\n');
}

function gerarCadastroMaquinaPraxis(dados: DadosApreciacao): string {
  const revisao = `revisão ${dados.revisao ?? 1}${dados.dataEmissao ? ` em ${formatarDataCurta(dados.dataEmissao)}` : ''}`;
  const linhas = [
    ['Máquina', dados.nomeMaquina || dados.maquina?.nome || dados.maquina?.descricao || 'N/I'],
    ['Tipo', dados.tipoMaquina || dados.maquina?.modelo || 'N/I'],
    ['Identificação', dados.identificacaoMaquina || dados.ativo?.refSismanaNovo || dados.ativo?.refSismanaAntigo || 'N/I'],
    ['Setor', dados.setorMaquina || dados.maquina?.area || 'N/I'],
    ['Revisão', revisao],
  ];
  const linhasLatex = linhas.map(([rotulo, valor]) =>
    `\\rowcolor{praxisGrayLight}\\textcolor{praxisDarkBlue}{\\textbf{${escapeLatex(rotulo)}}} & ${escapeLatex(valor)} \\\\ \\hline`
  ).join('\n');

  return `
\\section{AVALIAÇÃO DE RISCOS DAS MÁQUINAS}

\\begin{tabularx}{\\linewidth}{|>{\\columncolor{praxisGray}}p{3.4cm}|X|}
\\hline
${linhasLatex}
\\end{tabularx}

\\vspace{0.35cm}
\\begin{tabularx}{\\linewidth}{X X}
\\centering\\textbf{Foto Panorâmica} & \\centering\\textbf{Foto Identificação} \\tabularnewline
${imagemOuPlaceholder(dados.fotoPanoramicaUrl, '4.8cm', 'Foto Panorâmica', PRAXIS_ASSETS.fallbackPanoramica)} & ${imagemOuPlaceholder(dados.fotoIdentificacaoUrl, '4.8cm', 'Foto Identificação', PRAXIS_ASSETS.fallbackIdentificacao)} \\\\
\\end{tabularx}

\\vspace{0.2cm}
\\praxisBarLight{Função}
${escapeLatexComQuebras(dados.funcaoMaquina || dados.maquina?.descricao || 'N/I')}

\\praxisBarLight{Limites da Máquina}
${gerarLimitesMaquina(dados)}

\\vspace{0.2cm}
\\textbf{LEGENDA:}\\\\
\\# OK---REGISTRO (EVIDENCIADO)\\\\
\\# N/I---NÃO INFORMADO\\\\
\\# N/A---NÃO APLICÁVEL\\\\
\\# SPD---SETOR DA PLACA DANIFICADO

\\praxisBarLight{Limites de Espaço}
${escapeLatexComQuebras(dados.limitesEspaco || 'A planta deve permitir o livre acesso a todos os pontos de operação e manutenção da máquina, conforme atividades a serem realizadas, mesmo com as proteções abertas. Deve-se respeitar um espaçamento ao longo do perímetro da máquina com 1200mm para circulação (principalmente se forem acessos principais e/ou rotas de fuga); para outros acessos, o valor mínimo aceitável é de 600mm. Pessoas em treinamento somente poderão operar a máquina com supervisão permanente de uma pessoa capacitada e autorizada (mínimo), sendo a responsabilidade do treinamento somente por um PLH (Profissional Legalmente Habilitado).')}

\\praxisBarLight{Limites Adicionais}
${escapeLatexComQuebras(dados.limitesAdicionais || 'O equipamento requer operadores treinados e qualificados, com conhecimento das funções de operação, segurança e manutenção do equipamento. A operação do equipamento deve ser realizada somente em ambiente seco, protegido de influências externas e intempéries. Intervenções de limpeza e/ou manutenção devem ser informadas previamente, planejadas e cumpridas conforme manual e/ou orientação técnica de um Responsável Técnico, seguida de Ordem de Serviço (OS) específica emitida por um Profissional de Planejamento.')}
\\clearpage
`;
}

function gerarListaPartesPraxis(riscos: Risco[]): string {
  const linhas = riscos.map((risco) => {
    const classificacao = classificarPraxis(risco.hrnAtual);
    const plr = escapeLatex(risco.plr || '');
    const sil = escapeLatex(risco.sil || '');
    const cat = escapeLatex(risco.categoriaSeguranca || '');
    // IMPORTANTE: não passar a string srpcs pelo escapeLatex; só os valores.
    const srpcs = `PLr ${plr} \\hspace{0.25cm} SIL ${sil} \\hspace{0.25cm} Cat ${cat}`;
    const conforme = conformidadeMarca(!!risco.conformeAtual);
    const icone = tipoPerigoIcone(risco.tipoPerigo, '0.22cm');
    return [
      `\\rowcolor{praxisGrayLight}\\textbf{${escapeLatex(risco.parte || 'Sem parte definida')}} & \\multicolumn{2}{r|}{\\scriptsize ${srpcs}} \\\\ \\hline`,
      `${conforme}~${icone}~${escapeLatex(risco.eventoPerigoso || risco.consequencias || 'Risco não especificado')} & ${n(risco.hrnAtual)} & \\cellcolor{${classificacao.cor}}\\textcolor{${classificacao.textoCor}}{${escapeLatex(classificacao.texto)}} \\\\ \\hline`,
    ].join('\n');
  }).join('\n');

  return `
\\praxisTitle{LISTA DE PARTES}
\\begin{xltabular}{\\linewidth}{|X|p{1.8cm}|p{3.8cm}|}
\\hline
\\rowcolor{praxisBlue}\\textcolor{white}{\\textbf{Descrição do Risco}} & \\textcolor{white}{\\textbf{HRN}} & \\textcolor{white}{\\textbf{Classificação}} \\\\ \\hline
${linhas || `Nenhuma parte cadastrada & - & - \\\\ \\hline`}
\\end{xltabular}
\\clearpage
`;
}

function normalizarChecklist(dados: DadosApreciacao): Array<{ codigo: string; descricao: string; status: string; observacao: string }> {
  const grupos = dados.checklistTecnicoAdmin?.grupos;
  if (Array.isArray(grupos) && grupos.length > 0) {
    const itens = grupos.flatMap((grupo: any) => (grupo.itens || []).map((item: any) => ({
      codigo: item.codigo || '',
      descricao: item.descricao || '',
      status: item.status || 'N/A',
      observacao: item.observacao || '',
    })));
    // Se os códigos vieram em formato "L.1, AC.1..." (UI antiga), substitui pela tabela oficial.
    const usaPadraoOficial = itens.length > 0 && itens.every((it: any) => /^\d+$/.test(String(it.codigo).trim()));
    if (usaPadraoOficial) return itens;
  }
  return CHECKLIST_PRAXIS_PADRAO.map(([codigo, descricao]) => ({
    codigo,
    descricao,
    status: 'N/A',
    observacao: '',
  }));
}

function marca(status: string, alvo: 'OK' | 'NOK' | 'N/A'): string {
  return status === alvo ? '\\xis' : '';
}

function gerarChecklistPraxis(dados: DadosApreciacao): string {
  const linhas = normalizarChecklist(dados).map((item, idx) => {
    const corLinha = idx % 2 === 0 ? 'praxisGrayLight' : 'white';
    return `\\rowcolor{${corLinha}}${escapeLatex(item.codigo)} & ${escapeLatex(item.descricao)} & ${marca(item.status, 'OK')} & ${marca(item.status, 'NOK')} & ${marca(item.status, 'N/A')} & ${escapeLatex(item.observacao || '')} \\\\ \\hline`;
  }).join('\n');

  return `
\\praxisTitle{CHECKLIST TÉCNICO/ADMINISTRATIVO}
\\begin{center}
\\includegraphics[width=0.62\\linewidth,height=1.1cm,keepaspectratio]{${PRAXIS_ASSETS.legendaChecklist}}
\\end{center}

\\small
\\begin{xltabular}{\\linewidth}{|p{1.1cm}|X|p{0.75cm}|p{0.85cm}|p{0.85cm}|p{3.0cm}|}
\\hline
\\rowcolor{praxisBlue}\\textcolor{white}{\\textbf{Item}} & \\textcolor{white}{\\textbf{Descrição}} & \\textcolor{white}{\\textbf{OK}} & \\textcolor{white}{\\textbf{NOK}} & \\textcolor{white}{\\textbf{N/A}} & \\textcolor{white}{\\textbf{Observações}} \\\\ \\hline
${linhas}
\\end{xltabular}
\\normalsize
\\clearpage
`;
}

function hrnTabela(risco: Risco, futuro = false): string {
  const anyRisco = risco as any;
  const po = futuro ? anyRisco.probabilidadeFuturo ?? anyRisco.probabilidadeFutura : risco.probabilidadeAtual;
  const fe = futuro ? anyRisco.frequenciaFuturo ?? anyRisco.frequenciaFutura : risco.frequenciaAtual;
  const gs = futuro ? anyRisco.gravidadeFuturo ?? anyRisco.gravidadeFutura : risco.gravidadeAtual;
  const np = futuro ? anyRisco.exposicaoFuturo ?? anyRisco.exposicaoFutura : risco.exposicaoAtual;
  const hrn = futuro ? risco.hrnFuturo : risco.hrnAtual;
  const classificacao = classificarPraxis(hrn);

  return `
\\begin{tabularx}{\\linewidth}{|X|p{1.7cm}|X|p{1.7cm}|}
\\hline
Probabilidade da Ocorrência (PO) & ${n(po)} & Frequência de Exposição (FE) & ${n(fe)} \\\\ \\hline
Grau de Severidade (GS) & ${n(gs)} & Nº de Pessoas Expostas ao Risco (NP) & ${n(np)} \\\\ \\hline
\\rowcolor{praxisGrayLight}\\multicolumn{2}{|l|}{\\textbf{Hazard Rating Number - HRN (PO x FE x GS x NP)}} & \\textbf{${n(hrn)}} & \\cellcolor{${classificacao.cor}}\\textcolor{${classificacao.textoCor}}{\\textbf{${escapeLatex(classificacao.texto)}}} \\\\ \\hline
\\end{tabularx}
`;
}

function gerarParteIndividualPraxis(risco: Risco, index: number): string {
  const fotos = [risco.fotoAtualUrl1, risco.fotoAtualUrl2, risco.fotoAtualUrl3, risco.fotoAtualUrl4].filter(Boolean) as string[];
  const fotoBlock = fotos[0]
    ? `\\includegraphics[height=5.2cm,width=0.55\\linewidth,keepaspectratio]{${fotos[0]}}`
    : `\\includegraphics[height=5.2cm,width=0.55\\linewidth,keepaspectratio]{${imagemRiscoFallback(risco.tipoPerigo)}}`;
  const cicloVida = (risco.cicloVidaArray && risco.cicloVidaArray.length > 0 ? risco.cicloVidaArray.join(', ') : risco.cicloVida) || 'N/I';
  const status = risco.conformeAtual ? 'EM CONFORMIDADE' : 'NÃO CONFORMIDADE';
  const statusColor = risco.conformeAtual ? 'praxisGreen' : 'praxisRed';
  const tituloParte = risco.parte || `Parte ${index + 1}`;
  const tipoPerigo = risco.tipoPerigo || 'Perigos';
  const iconePerigo = tipoPerigoIcone(tipoPerigo, '0.55cm');
  const conformeIcone = risco.conformeAtual
    ? `\\includegraphics[height=0.35cm]{${PRAXIS_ASSETS.iconCheckVerde}}`
    : `\\includegraphics[height=0.35cm]{${PRAXIS_ASSETS.iconCirculoVermelho}}`;

  const perigosBullets = bulletsDeTexto(risco.perigosEncontrados, 'Não informado');
  const consequenciasBullets = bulletsDeTexto(risco.consequencias, 'Não informado');
  const adequacoesBullets = bulletsDeTexto(risco.adequacoesTecnicas || risco.medidasControle, 'Não informado');

  return `
\\praxisBarBlue{${escapeLatex(tituloParte)}}
\\addcontentsline{toc}{subsection}{${escapeLatex(tituloParte)}}
\\begin{center}
${fotoBlock}
\\end{center}

\\textbf{CICLO DE VIDA:} ${escapeLatex(cicloVida)}

\\praxisBarBlue{${escapeLatex(tipoPerigo)}}
\\praxisBar{\\textbf{Evento Perigoso:} ${escapeLatex(risco.eventoPerigoso || 'N/I')}}

\\noindent
\\begin{minipage}[c]{0.18\\linewidth}
\\centering
${iconePerigo}
\\end{minipage}%
\\hfill
\\begin{minipage}[c]{0.4\\linewidth}
\\raggedleft
\\textcolor{${statusColor}}{\\textbf{${escapeLatex(status)}}}\\ ${conformeIcone}
\\end{minipage}

\\praxisTitle{HRN NA CONDIÇÃO ATUAL DA MÁQUINA}
${hrnTabela(risco, false)}

\\vspace{0.2cm}
\\noindent
\\begin{minipage}[t]{0.48\\linewidth}
\\textbf{Perigos encontrados}\\\\
${perigosBullets}
\\end{minipage}\\hfill
\\begin{minipage}[t]{0.48\\linewidth}
\\textbf{Potenciais Consequências}\\\\
${consequenciasBullets}
\\end{minipage}

\\vspace{0.2cm}
\\textbf{Adequações técnicas}
${adequacoesBullets}

${risco.adequacoesNormativas ? `\\vspace{0.15cm}\\textbf{Adequações administrativas}\n${bulletsDeTexto(risco.adequacoesNormativas, 'Não informado')}` : ''}

\\praxisTitle{HRN APÓS RECOMENDAÇÕES IMPLEMENTADAS}
${hrnTabela(risco, true)}
\\clearpage
`;
}

function gerarConclusaoPraxis(dados: DadosApreciacao): string {
  const cidade = dados.cidadeAssinatura || 'Boituva';
  const data = formatarDataPorExtenso(dados.dataAprovacao || dados.dataEmissao, cidade);
  const consultor = obterConsultor(dados);
  const funcaoCurta = funcaoConsultorCurta(consultor.funcao);
  const conclusao = dados.conclusoesFinais || `Para a(s) máquina(s) apresentada(s) neste documento de Apreciação de Riscos, cada parte ou atividade apresentada no capítulo 6 deverá estar com o estado de "Em conformidade", na cor verde. Caso estejam como "Não conformidade", na cor vermelha, o proprietário da máquina deverá providenciar as adequações sugeridas na respectiva parte ou atividade a fim de atender os requisitos normativos.

Objetivando reduzir os riscos, foram considerados os aspectos das normas tipo C, quando aplicáveis, a ABNT NBR ISO 12100 e a metodologia HRN para a quantificação do grau dos riscos. Conforme indica o "estado da técnica", foram aplicados os meios técnicos mais eficazes possíveis para a redução de riscos a um nível aceitável, permitindo assim declarar que, após todas as partes e atividades do capítulo 6 estejam em conformidade, a(s) máquina(s) esteja(m) de acordo com os requisitos da NR-12 contidos neste relatório.

Importante salientar que a Apreciação de Riscos apresentada contempla os itens mais relevantes que estão relacionados aos perigos elétricos e mecânicos.

Esse documento abrange a vida útil da(s) máquina(s) avaliada(s), a partir da data de fabricação, conforme padrão do Instituto Brasileiro de Avaliações e Perícias de Engenharia de São Paulo (IBAPE/SP), caso não exista esta informação descrita pelo fabricante.`;

  const exclusao = dados.exclusaoResponsabilidade || `As funcionalidades descritas da(s) máquina(s) ou equipamento(s) nesta documentação são baseadas nos dados fornecidos pelo fabricante ou pelo proprietário da máquina e as informações coletadas no local durante a apreciação de riscos que aconteceu na data informada no capítulo 6. As ilustrações representadas e fotos da(s) máquina(s) referem-se às condições registradas no momento da inspeção.

A SCHMERSAL salienta que a lista de partes ou atividades deste documento de Apreciação de Riscos destina-se a auxiliar as pessoas responsáveis e/ou seus representantes no cumprimento dos requisitos legais da NR 12.

Para máquina(s) nova(s) ou que será(ão) adequada(s) a partir da(s) recomendação(ões) desse documento. A SCHMERSAL não assegura ou confirma que a(s) adequação(ões) será(ão) executada(s) conforme solicitado.

Não está sendo avaliada a resistência dos materiais das proteções fixas, das proteções móveis, dos dispositivos eletromecânicos instalados e das dimensões dos meios de acesso permanentes.

Outros perigos citados na ABNT NBR ISO 12100 não estão sendo avaliados pois necessitam de uma avaliação específica.

A SCHMERSAL não aceita responsabilidade pela forma que este documento de Apreciação de Riscos possa ser utilizado ou por qualquer conteúdo alterado após entrega na data descrita.

Todo esforço é feito para assegurar que as informações contidas neste documento sejam exatas e atualizadas utilizando diretrizes, normas técnicas e regulamentações relevantes para essa(s) máquina(s). A responsabilidade da Schmersal se limita às informações e termos descritos neste documento.`;

  return `
\\section{CONCLUSÃO(ÕES) FINAL(IS)}
${escapeLatexComQuebras(conclusao)}

\\section{EXCLUSÃO DE RESPONSABILIDADE}
${escapeLatexComQuebras(exclusao)}

\\vspace{0.8cm}
\\begin{flushright}
${escapeLatex(data)}
\\end{flushright}

\\vspace{1.5cm}
\\rule{7.5cm}{0.4pt}\\\\
${escapeLatex(consultor.nome.toUpperCase())}\\\\
${escapeLatex(funcaoCurta.toUpperCase())}
`;
}

export function gerarLatexPraxis(dados: DadosApreciacao, riscos: Risco[]): string {
  let latex = gerarPreambuloPraxis(dados);
  latex += `\\begin{document}
`;
  latex += gerarCapaPraxis(dados, riscos);
  latex += `\\pagenumbering{arabic}
\\setcounter{page}{1}
\\pagestyle{fancy}
\\tableofcontents
\\clearpage
`;
  latex += gerarListaAbreviaturas();
  latex += gerarInformacoesDocumento(dados);
  latex += gerarInformacoesGerais();
  latex += gerarExemplosPerigos();
  latex += gerarCriteriosHrn();
  latex += gerarTermosDefinicoes();
  latex += gerarCadastroMaquinaPraxis(dados);
  latex += gerarListaPartesPraxis(riscos);
  latex += gerarChecklistPraxis(dados);
  riscos.forEach((risco, index) => {
    latex += gerarParteIndividualPraxis(risco, index);
  });
  latex += gerarConclusaoPraxis(dados);
  latex += `\\end{document}`;
  return latex;
}
